import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Users, Clock } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { StatusBadge } from "../../components/ui/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_OPTIONS = ["Draft", "Published", "Fully Booked", "Closed", "Cancelled"];

export default function AdminActivities() {
  const navigate = useNavigate();
  const { activities, slotBooked, slotRemaining } = useApp();
  const [editing, setEditing] = useState(null); // activity object or {} for new
  const [form, setForm] = useState({});

  const openEdit = (a) => {
    setForm(a
      ? { name: a.name, location: a.location, duration: a.duration, ageRecommendation: a.ageRecommendation, bookingRequired: a.bookingRequired, status: a.bookingRequired ? "Published" : "Published" }
      : { name: "", location: "", duration: "", ageRecommendation: "", bookingRequired: true, status: "Draft" });
    setEditing(a || {});
  };

  const save = () => {
    toast.success(editing.id ? "Perubahan aktivitas disimpan (prototype)." : "Aktivitas baru dibuat (prototype).");
    setEditing(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl text-slate-800">Activity Management</h1>
          <p className="text-slate-500 font-semibold">Kelola aktivitas, slot waktu, dan kapasitas.</p>
        </div>
        <button
          onClick={() => openEdit(null)}
          data-testid="admin-new-activity"
          className="flex items-center gap-2 bg-sea text-white font-display rounded-lg px-4 py-2.5 hover:bg-sea-deep transition-colors"
        >
          <Plus className="w-5 h-5" /> New Activity
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {activities.map((a) => {
          const totalCap = a.slots.reduce((s, sl) => s + sl.capacity, 0);
          const totalBooked = a.slots.reduce((s, sl) => s + slotBooked(a.id, sl.id), 0);
          const pct = totalCap ? Math.round((totalBooked / totalCap) * 100) : 0;
          return (
            <div key={a.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`admin-activity-${a.slug}`}>
              <div className="p-5 flex flex-wrap items-center gap-4">
                <img src={a.icon} alt="" className="w-12 h-12 object-contain" />
                <div className="flex-1 min-w-[160px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg text-slate-800">{a.name}</h3>
                    <StatusBadge status={a.bookingRequired ? "Active" : "no-booking"} />
                  </div>
                  <p className="text-sm text-slate-500 font-semibold">{a.location} · {a.duration} · {a.ageRecommendation}</p>
                </div>
                {a.bookingRequired && (
                  <div className="w-full sm:w-48">
                    <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                      <span>{totalBooked}/{totalCap} booked</span><span>{pct}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-sea rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(a)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="Edit" data-testid={`edit-${a.slug}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  {a.bookingRequired && (
                    <button
                      onClick={() => navigate(`/admin/activities/${a.slug}/participants`)}
                      className="flex items-center gap-1.5 p-2 px-3 rounded-lg bg-sky/25 text-sea-deep font-semibold text-sm hover:bg-sky/40"
                      data-testid={`participants-${a.slug}`}
                    >
                      <Users className="w-4 h-4" /> Participants
                    </button>
                  )}
                </div>
              </div>

              {/* time slots */}
              {a.bookingRequired && (
                <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-3">
                  <p className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1 mb-2"><Clock className="w-3.5 h-3.5" /> Time Slots</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {a.slots.map((s) => {
                      const booked = slotBooked(a.id, s.id);
                      const rem = slotRemaining(a.id, s.id);
                      return (
                        <div key={s.id} className="bg-white rounded-lg border border-slate-200 p-2.5 text-sm">
                          <p className="font-display text-slate-700">{s.start}–{s.end}</p>
                          <p className="text-xs font-bold text-slate-500">{booked}/{s.capacity} · {rem} left</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-800">{editing?.id ? "Edit Activity" : "New Activity"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <AdminInput label="Nama" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="af-name" />
            <AdminInput label="Lokasi" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="af-location" />
            <AdminInput label="Durasi" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="af-duration" />
            <AdminInput label="Rekomendasi Umur" value={form.ageRecommendation} onChange={(v) => setForm({ ...form, ageRecommendation: v })} testid="af-age" />
            <label className="block">
              <span className="text-sm font-bold text-slate-600">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 font-semibold text-slate-700" data-testid="af-status">
                {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.bookingRequired} onChange={(e) => setForm({ ...form, bookingRequired: e.target.checked })} className="w-5 h-5 accent-sea" data-testid="af-booking" />
              <span className="text-sm font-bold text-slate-600">Perlu Reservasi</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-slate-200 font-semibold text-slate-600">Batal</button>
            <button onClick={save} className="px-4 py-2 rounded-lg bg-sea text-white font-display" data-testid="af-save">Simpan</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AdminInput = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-sm font-bold text-slate-600">{label}</span>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1 w-full rounded-lg border border-slate-300 focus:border-sea outline-none px-3 py-2.5 font-semibold text-slate-700" />
  </label>
);
